import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/base/Modal';
import { availableRoles } from '@/mocks/accessControl';

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onCreateOnly: (data: { name: string; email: string; phone: string; role: string; password: string }) => void;
  onCreateAndSend: (data: { name: string; email: string; phone: string; role: string; password: string }) => void;
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  let password = '';
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

export default function CreateUserModal({ open, onClose, onCreateOnly, onCreateAndSend }: CreateUserModalProps) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('');
  const [password, setPassword] = useState('');
  const [passwordGenerated, setPasswordGenerated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = useCallback(() => {
    setName('');
    setEmail('');
    setPhone('');
    setRole('');
    setPassword('');
    setPasswordGenerated(false);
    setShowPassword(false);
    setErrors({});
  }, []);

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setPassword(newPassword);
    setPasswordGenerated(true);
    setShowPassword(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.password;
      return next;
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = t('common.required');
    if (!email.trim()) newErrors.email = t('common.required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Invalid email format';
    if (!phone.trim()) newErrors.phone = t('common.required');
    if (!role) newErrors.role = t('common.required');
    if (!password) newErrors.password = t('common.required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateOnly = () => {
    if (!validate()) return;
    let pwd = password;
    if (!passwordGenerated) {
      pwd = generatePassword();
      setPassword(pwd);
      setPasswordGenerated(true);
    }
    onCreateOnly({ name: name.trim(), email: email.trim(), phone: phone.trim(), role, password: pwd });
    resetForm();
    onClose();
  };

  const handleCreateAndSend = () => {
    if (!validate()) return;
    let pwd = password;
    if (!passwordGenerated) {
      pwd = generatePassword();
      setPassword(pwd);
      setPasswordGenerated(true);
    }
    onCreateAndSend({ name: name.trim(), email: email.trim(), phone: phone.trim(), role, password: pwd });
    resetForm();
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const canSubmit = name.trim() && email.trim() && phone.trim() && role && password;

  const footerContent = (
    <div className="flex items-center justify-between w-full">
      <button
        onClick={handleGeneratePassword}
        className={`inline-flex items-center gap-2 h-9 px-3 text-sm font-label font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
          passwordGenerated
            ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
        }`}
        disabled={passwordGenerated}
      >
        <i className="ri-refresh-line" />
        {passwordGenerated ? t('accessControl.autoGeneratedPassword') : 'Generate Password'}
      </button>
      <div className="flex items-center gap-3">
        <button
          onClick={handleClose}
          className="inline-flex items-center gap-2 h-9 px-4 bg-background-100 text-foreground-600 text-sm font-label font-medium rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleCreateOnly}
          disabled={!canSubmit}
          className={`inline-flex items-center gap-2 h-9 px-4 text-sm font-label font-medium rounded-lg transition-colors whitespace-nowrap ${
            canSubmit
              ? 'bg-background-200 text-foreground-700 hover:bg-background-300 cursor-pointer'
              : 'bg-background-200 text-foreground-400 cursor-not-allowed'
          }`}
        >
          <i className="ri-user-add-line" />
          {t('accessControl.createOnly')}
        </button>
        <button
          onClick={handleCreateAndSend}
          disabled={!canSubmit}
          className={`inline-flex items-center gap-2 h-9 px-4 text-sm font-label font-medium rounded-lg transition-colors whitespace-nowrap ${
            canSubmit
              ? 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer'
              : 'bg-background-200 text-foreground-400 cursor-not-allowed'
          }`}
        >
          <i className="ri-mail-send-line" />
          {t('accessControl.createAndSend')}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('accessControl.createUserTitle')}
      subtitle={t('accessControl.createUserSubtitle')}
      size="lg"
      footer={footerContent}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground-700 font-label">
              {t('common.fullName')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors((prev) => { const next = { ...prev }; delete next.name; return next; }); }}
              placeholder={t('accessControl.userNamePlaceholder')}
              className={`xl-input text-sm ${errors.name ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
            />
            {errors.name && <span className="text-xs text-red-500 font-label">{errors.name}</span>}
          </div>

          {/* Email */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground-700 font-label">
              {t('common.emailAddress')} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrors((prev) => { const next = { ...prev }; delete next.email; return next; }); }}
              placeholder={t('accessControl.emailPlaceholder')}
              className={`xl-input text-sm ${errors.email ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
            />
            {errors.email && <span className="text-xs text-red-500 font-label">{errors.email}</span>}
          </div>

          {/* Phone */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground-700 font-label">
              {t('common.contactNumber')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setErrors((prev) => { const next = { ...prev }; delete next.phone; return next; }); }}
              placeholder={t('accessControl.phonePlaceholder')}
              className={`xl-input text-sm ${errors.phone ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
            />
            {errors.phone && <span className="text-xs text-red-500 font-label">{errors.phone}</span>}
          </div>

          {/* Role */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-foreground-700 font-label">
              {t('accessControl.role')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={role}
                onChange={(e) => { setRole(e.target.value); setErrors((prev) => { const next = { ...prev }; delete next.role; return next; }); }}
                className={`xl-input cursor-pointer text-sm appearance-none pr-8 ${errors.role ? 'border-red-400 focus:border-red-400 focus:ring-red-200' : ''}`}
              >
                <option value="">{t('accessControl.selectRole')}</option>
                {availableRoles.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none" />
            </div>
            {errors.role && <span className="text-xs text-red-500 font-label">{errors.role}</span>}
          </div>
        </div>

        {/* Password Section */}
        <div className="pt-2 border-t border-background-200">
          <label className="text-sm font-medium text-foreground-700 font-label mb-2 block">
            {t('accessControl.autoGeneratedPassword')} <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-foreground-500 font-label mb-3">{t('accessControl.passwordNote')}</p>

          {!passwordGenerated ? (
            <button
              onClick={handleGeneratePassword}
              className="inline-flex items-center gap-2 h-9 px-4 bg-accent-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-key-2-line" />
              Generate Password
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-xs">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  readOnly
                  className="xl-input text-sm pr-10 bg-background-100 font-mono tracking-wider"
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                </button>
              </div>
              <button
                onClick={handleGeneratePassword}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer flex-shrink-0"
                title="Regenerate password"
              >
                <i className="ri-refresh-line text-base" />
              </button>
            </div>
          )}

          {errors.password && <span className="block mt-1.5 text-xs text-red-500 font-label">{errors.password}</span>}
        </div>

        {/* Summary */}
        {name && email && role && passwordGenerated && (
          <div className="p-3 bg-background-100 rounded-lg border border-background-200">
            <p className="text-xs font-label font-semibold text-foreground-500 uppercase tracking-wider mb-2">Summary</p>
            <div className="flex flex-col gap-1 text-sm">
              <p className="text-foreground-700 font-label">
                <span className="text-foreground-400">{t('common.fullName')}: </span>
                <span className="font-medium">{name.trim()}</span>
              </p>
              <p className="text-foreground-700 font-label">
                <span className="text-foreground-400">{t('common.emailAddress')}: </span>
                <span className="font-medium">{email.trim()}</span>
              </p>
              <p className="text-foreground-700 font-label">
                <span className="text-foreground-400">{t('accessControl.role')}: </span>
                <span className="font-medium">{role}</span>
              </p>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}